import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import type { SurveyOption, SurveyQuestion } from '../../types.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';

type MaxDiffValue = { best?: string; worst?: string };

interface MaxDiffFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: MaxDiffValue) => void;
}

export function MaxDiffField({ palette, question, value, onChange }: MaxDiffFieldProps): JSX.Element {
  const options: SurveyOption[] = question.settings?.options ?? [];
  const current: MaxDiffValue =
    value && typeof value === 'object' ? (value as MaxDiffValue) : {};

  const setBest = (next: string) => {
    onChange({ best: next, worst: current.worst === next ? undefined : current.worst });
  };
  const setWorst = (next: string) => {
    onChange({ best: current.best === next ? undefined : current.best, worst: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={headerRow(palette)}>
        <span style={headerLabel(palette, 'best')}>Best</span>
        <span style={headerLabel(palette, 'option')}>Option</span>
        <span style={headerLabel(palette, 'worst')}>Worst</span>
      </div>
      {options.map((option) => (
        <div key={option.value} style={row(palette)}>
          <RadioCell
            selected={current.best === option.value}
            onSelect={() => setBest(option.value)}
            palette={palette}
            tone="best"
          />
          <span style={{ flex: 1, color: palette.text, fontSize: 15 }}>{option.label}</span>
          <RadioCell
            selected={current.worst === option.value}
            onSelect={() => setWorst(option.value)}
            palette={palette}
            tone="worst"
          />
        </div>
      ))}
    </div>
  );
}

interface RadioCellProps {
  selected: boolean;
  onSelect: () => void;
  palette: Palette;
  tone: 'best' | 'worst';
}

function RadioCell({ selected, onSelect, palette, tone }: RadioCellProps): JSX.Element {
  const accent = tone === 'best' ? palette.primary : '#E45F5F';
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={tone === 'best' ? 'Mark as best' : 'Mark as worst'}
      className="chirp-btn"
      style={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        border: `2px solid ${selected ? accent : hexWithAlpha(palette.textMuted, 0.5)}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: selected ? hexWithAlpha(accent, 0.1) : 'transparent',
        cursor: 'pointer',
        flexShrink: 0,
        padding: 0,
      }}
    >
      {selected && <span style={{ width: 10, height: 10, borderRadius: '50%', background: accent }} />}
    </button>
  );
}

function row(palette: Palette): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: '40px 1fr 40px',
    alignItems: 'center',
    gap: 12,
    padding: '8px 4px',
    borderBottom: `1px solid ${hexWithAlpha(palette.textMuted, 0.12)}`,
  };
}

function headerRow(palette: Palette): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: '40px 1fr 40px',
    alignItems: 'center',
    gap: 12,
    padding: '4px 4px 8px',
    color: palette.textMuted,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: 600,
  };
}

function headerLabel(palette: Palette, role: 'best' | 'option' | 'worst'): CSSProperties {
  if (role === 'option') return { textAlign: 'left' };
  return { textAlign: 'center', color: role === 'best' ? palette.primary : '#E45F5F' };
}
